import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import RelatedCalculators from '../components/RelatedCalculators';
import { detectLocaleSync } from '../locales/config';

const COLORS = { bg: "#0a0a0f", text: "#e8e8f0", muted: "#9999b5", accent: "#00e5a0", border: "#1e1e2e" };

const faqItems = [
  {
    q: 'How does a personal loan payoff calculator work?',
    a: "Enter your loan balance, interest rate, and current monthly payment. The calculator shows exactly when you'll be debt-free and how much total interest you'll pay — plus how paying extra each month changes both.",
  },
  {
    q: 'Can I pay off a personal loan early?',
    a: 'In most cases yes — but check your loan agreement for prepayment penalties first. Many lenders charge a fee (typically 1–5% of the remaining balance) if you pay off early. If there\'s no penalty, early payoff can save significant interest.',
  },
  {
    q: 'How much interest will I save by paying extra?',
    a: 'Use the Extra Payment Scenarios section in the calculator to see exact figures. On a $10,000 loan at 15% APR, adding just $100/month extra can save over $2,000 in interest and cut your payoff time by 2+ years.',
  },
  {
    q: 'Should I use a personal loan to consolidate debt?',
    a: 'If the personal loan rate is lower than your existing debt rates (especially credit cards at 20%+), consolidation can save money. Enter the new consolidated balance and rate into the calculator to see your revised payoff timeline.',
  },
  {
    q: 'Does this work for multiple personal loans?',
    a: 'Yes — click "+ Add debt" to enter each loan separately. The calculator shows a combined payoff plan using either the Snowball or Avalanche method.',
  },
];

export default function PersonalLoanCalculator() {
  const locale = detectLocaleSync();
  return (
    <div style={{ background: COLORS.bg, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif", color: COLORS.text }}>
      <Helmet>
        <title>Free Personal Loan Payoff Calculator — ClearMyDebt</title>
        <meta name="description" content="Calculate exactly when you'll pay off your personal loan. Enter your balance, interest rate, and payment — see your debt-free date and total interest saved instantly." />
        <link rel="canonical" href="https://clearmydebt.app/personal-loan-payoff-calculator/" />
        <meta property="og:title" content="Free Personal Loan Payoff Calculator — ClearMyDebt" />
        <meta property="og:description" content="See your personal loan payoff date and how much interest extra payments save. Free, instant, no signup." />
        <meta property="og:url" content="https://clearmydebt.app/personal-loan-payoff-calculator/" />
        <meta property="og:image" content="https://clearmydebt.app/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://clearmydebt.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '48px 24px 0' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: COLORS.accent, fontWeight: 700, marginBottom: '12px' }}>
          <a href="/" style={{ color: COLORS.accent, textDecoration: 'none' }}>ClearMyDebt</a>
        </div>
        <h1 style={{ fontSize: 'clamp(24px,5vw,36px)', fontWeight: 900, letterSpacing: '-1px', margin: '0 0 12px', background: `linear-gradient(135deg,#fff,${COLORS.accent})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Free Personal Loan Payoff Calculator
        </h1>
        <p style={{ color: COLORS.muted, fontSize: '1.05rem', marginBottom: 0, lineHeight: 1.6 }}>
          Enter your personal loan balance, interest rate, and monthly payment to see exactly
          when you'll be debt-free — and how much interest you'll save by paying extra each month.
        </p>
      </div>

      <Calculator
        defaultDebtType="Personal Loan"
        lockedDebtType={false}
        locale={locale}
        showHelmet={false}
        showHeader={false}
      />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 24px 64px' }}>
        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How to use this calculator
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Enter your current loan balance, interest rate, and monthly payment amount.
          If you have multiple personal loans, click "+ Add debt" to include each one.
          Use the "Extra Payment" scenarios to see exactly how much sooner you'll pay off
          the loan and how many dollars in interest you'll save.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 12px' }}>
          How personal loan interest works
        </h2>
        <p style={{ color: COLORS.muted, lineHeight: 1.7, marginBottom: 28 }}>
          Personal loans typically use simple interest, calculated on the outstanding principal balance.
          Average personal loan APRs in the US range from about 12% to 22% depending on credit score.
          On a $10,000 loan at 16% over 5 years, you'd pay roughly $4,400 in interest on the standard
          schedule — extra payments go directly toward reducing that principal, cutting your interest
          proportionally.
        </p>

        <h2 style={{ color: COLORS.text, fontSize: '1.3rem', fontWeight: 700, margin: '0 0 20px' }}>
          Frequently Asked Questions
        </h2>
        {faqItems.map(({ q, a }) => (
          <div key={q} style={{ marginBottom: 20, padding: '16px', background: '#13131a', borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <h3 style={{ color: COLORS.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 8px' }}>{q}</h3>
            <p style={{ color: COLORS.muted, lineHeight: 1.6, margin: 0 }}>{a}</p>
          </div>
        ))}

      </div>

      <RelatedCalculators locale={locale} exclude="/personal-loan-payoff-calculator/" />
    </div>
  );
}
